import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "./ui/button";
import { Eraser } from "lucide-react";

export const SignaturePad = forwardRef(function SignaturePad({ testId = "signature-pad", disabled = false, heightClass = "h-36" }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  // Le canvas peut être monté dans un dialogue encore en animation de zoom : sa
  // taille à l'écran (getBoundingClientRect) est alors transformée. On dimensionne
  // donc le bitmap sur la taille de mise en page (clientWidth/Height, insensible
  // aux transformations et à la bordure) et on convertit chaque point souris/doigt
  // par le rapport réel bitmap/écran au moment du tracé.
  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
    canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.2 * dpr;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    // Position dans la zone de dessin (hors bordure), puis conversion en pixels du bitmap.
    const borderX = (rect.width - canvas.clientWidth * (rect.width / canvas.offsetWidth)) / 2;
    const borderY = (rect.height - canvas.clientHeight * (rect.height / canvas.offsetHeight)) / 2;
    const innerW = rect.width - 2 * borderX;
    const innerH = rect.height - 2 * borderY;
    return {
      x: ((point.clientX - rect.left - borderX) / innerW) * canvas.width,
      y: ((point.clientY - rect.top - borderY) / innerH) * canvas.height,
    };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (disabled || !drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setEmpty(false);
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    clear,
    toDataURL: () => (empty ? "" : canvasRef.current.toDataURL("image/png")),
  }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid={testId}
        className={`w-full ${heightClass} bg-slate-50 dark:bg-zinc-800 border-2 border-dashed border-border rounded-lg touch-none ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-crosshair"}`}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!disabled && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid={`${testId}-clear-button`}
          onClick={clear}
          className="absolute top-2 right-2 gap-1"
        >
          <Eraser className="w-3.5 h-3.5" /> Effacer
        </Button>
      )}
    </div>
  );
});
